'use client';

import { ValidationError } from '@eliadmin/core/src/api/errors';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Textarea } from '@eliadmin/core/src/components/ui/textarea';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { CONTACT_CONFIG } from '@eliadmin/core/src/constants/contact-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import Link from '@eliadmin/core/src/components/link';
import { useRouter } from 'next/navigation';

import { useForm } from 'react-hook-form';
import { z } from 'zod';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { getDefaultUrlForTosOrGdprLocalized } from '@eliadmin/core/src/utils/misc-utils';
import { PhoneInputField } from '@eliadmin/core/src/components/ui/phone-input/phone-input';
import { RiSendPlane2Line } from 'react-icons/ri';

const contactSchema = (VM: FormValidationMessages) =>
  z.object({
    name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    surname: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    tel: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    text: z.string(),
    gdpr: z.boolean().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
    'g-recaptcha-response': z.string().default(''),
  });

export type ContactFormValues = z.infer<ReturnType<typeof contactSchema>>;

export function ContactForm() {
  const { t } = useTranslations();

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema(formValidationMessages)),
    defaultValues: {
      name: '',
      surname: '',
      email: '',
      tel: '',
      text: '',
      gdpr: false,
    },
  });

  const { mutateAsync } = useMutation({
    mutationFn: async (
      input: Parameters<
        typeof import('@eliadmin/core/src/api/forms/forms-api').sendContactMessage
      >[0],
    ) => {
      const { sendContactMessage } = await import('@eliadmin/core/src/api/forms/forms-api');
      return sendContactMessage(input);
    },
  });

  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const handleSubmit = form.handleSubmit(async data => {
    try {
      await mutateAsync({
        data: {
          ...data,
        },
        locale,
      });
      toast({
        title: t('text658'),
        variant: 'success',
      });
      router.push(ROUTES.HOME());
    } catch (err) {
      // handle 422
      if (err instanceof ValidationError) {
        for (const [field, errors] of Object.entries(err.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text657'),
        description: (err as any)?.message,
        variant: 'destructive',
      });
    }
  });

  //TODO: Match values with backend (removed surname, added tel)

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit}
        className={cn('grid w-full max-w-[540px] grid-cols-12 gap-4', 'ea-contact-form')}
      >
        <FormField
          control={form.control}
          name={'name'}
          render={({ field }) => (
            <FormItem
              required
              className={cn(
                'col-span-12 sm:col-span-6',
                'ea-contact-form__field ea-contact-form__name-field',
              )}
            >
              {/* <FormLabel>
                <span>{t('text33')}</span>
              </FormLabel> */}

              <div
                className={cn(
                  'flex w-full flex-row items-center space-x-2',
                  'ea-contact-form__input-wrapper ea-contact-form__name-input-wrapper',
                )}
              >
                <FormControl>
                  <Input
                    {...(field as any)}
                    placeholder={t('text33') + ' *'}
                    aria-label={t('text33')}
                    rounded={CONTACT_CONFIG.form.input.rounded}
                    className={cn('', 'ea-contact-form__input ea-contact-form__name-input')}
                  />
                </FormControl>
              </div>

              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={'surname'}
          render={({ field }) => (
            <FormItem
              required
              className={cn(
                'col-span-12 sm:col-span-6',
                'ea-contact-form__surname-field ea-contact-form__field',
              )}
            >
              {/* <FormLabel>
                <span>{t('text34')}</span>
              </FormLabel> */}

              <div
                className={cn(
                  'flex w-full flex-row items-center space-x-2',
                  'ea-contact-form__input-wrapper ea-contact-form__surname-input-wrapper',
                )}
              >
                <FormControl>
                  <Input
                    {...(field as any)}
                    placeholder={t('text34') + ' *'}
                    aria-label={t('text34')}
                    rounded={CONTACT_CONFIG.form.input.rounded}
                    className={cn('', 'ea-contact-form__input ea-contact-form__surname-input')}
                  />
                </FormControl>
              </div>

              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={'email'}
          render={({ field }) => (
            <FormItem
              required
              className={cn('col-span-12', 'ea-contact-form__email-field ea-contact-form__field')}
            >
              {/* <FormLabel>
                <span>{t('text35')}</span>
              </FormLabel> */}

              <div
                className={cn(
                  'flex w-full flex-row items-center space-x-2',
                  'ea-contact-form__input-wrapper ea-contact-form__email-input-wrapper',
                )}
              >
                <FormControl>
                  <Input
                    {...(field as any)}
                    type="email"
                    placeholder={t('text35') + ' *'}
                    aria-label={t('text35')}
                    rounded={CONTACT_CONFIG.form.input.rounded}
                    className={cn('', 'ea-contact-form__email-input ea-contact-form__input')}
                  />
                </FormControl>
              </div>

              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="tel"
          render={({ field }) => (
            <FormItem required className="col-span-12 w-full">
              <FormControl>
                <PhoneInputField
                  ref={field.ref}
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  disabled={field.disabled}
                  aria-disabled={field.disabled}
                  inputProps={{ name: field.name, 'aria-label': t('text36') }}
                  type="tel"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={'text'}
          render={({ field }) => (
            <FormItem
              required
              className={cn('col-span-12', 'ea-contact-form__field ea-contact-form__text-field')}
            >
              {/* <FormLabel>
                <span>{t('text80')}</span>
              </FormLabel> */}

              <div
                className={cn(
                  'flex w-full flex-row items-center space-x-2',
                  'ea-contact-form__input-wrapper ea-contact-form__text-input-wrapper',
                )}
              >
                <FormControl>
                  <Textarea
                    placeholder={t('text615') + ' *'}
                    aria-label={t('text615')}
                    {...(field as any)}
                    rows={5}
                    rounded={CONTACT_CONFIG.form.input.rounded}
                    className="placeholder:font-light"
                  />
                </FormControl>
              </div>

              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="gdpr"
          render={({ field }) => (
            <FormItem
              className={cn(
                'col-span-12 flex items-center gap-2',
                'ea-contact-form__gdpr-checkbox-item ea-contact-form__checkbox-item',
              )}
            >
              <FormControl>
                <Checkbox
                  ref={field.ref}
                  name={field.name}
                  disabled={field.disabled}
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  className={cn(
                    'rounded-none',
                    'ea-contact-form__gdpr-checkbox ea-contact-form__checkbox',
                  )}
                />
              </FormControl>
              <FormLabel className={cn('font-light', 'ea-contact-form__gdpr-checkbox-label')}>
                {t('text613')}{' '}
                <Link
                  href={getDefaultUrlForTosOrGdprLocalized(ROUTES?.GDPR?.() ?? '/', locale, 'gdpr')}
                  className={cn('', 'ea-contact-form__gdpr-link')}
                  prefetch={false}
                >
                  <span className="font-light text-primary">{t('text614')}</span>
                </Link>
              </FormLabel>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={'g-recaptcha-response'}
          render={() => (
            <FormItem required className={cn('col-span-12')}>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="col-span-12 flex w-full items-center justify-center">
          <Button
            type="submit"
            className={cn('col-span-12', 'ea-contact-form__submit')}
            isLoading={form.formState.isSubmitting}
          >
            {t('text185')} <RiSendPlane2Line size={20} className="ml-2" />
          </Button>
        </div>
      </form>
    </Form>
  );
}
